import {CancellationToken, checkCancellation, WithCancellation} from "../../tasks";
import {RewritableExtension, withExtension} from "./file-name.util";

/**
 * Image types this layer is willing to *emit*.
 *
 * WebP is deliberately excluded even though every browser here can encode it: the attachment
 * endpoint rejects it. Narrowing the type makes it impossible to produce an unuploadable image
 * from this layer at all, rather than relying on a downstream check to catch it.
 */
export type EncodableImageType = 'image/jpeg' | 'image/png';

const EncodableImageTypes: readonly string[] = ['image/jpeg', 'image/png'];

const ExtensionByType: Readonly<Record<EncodableImageType, RewritableExtension>> = {
  'image/jpeg': 'jpg',
  'image/png': 'png'
};

const DEFAULT_ENCODE_QUALITY = 0.8;

/**
 * Wall-clock budget for {@link fitBitmapToBytes}' search. Comfortably inside the caller's outer
 * timeout so an unusually slow device degrades to a slightly larger (but valid) upload instead of
 * a hard timeout with nothing to show for it.
 */
const DEFAULT_FIT_BUDGET_MS = 8_000;

/** Floor for the fallback shrink, so a pathological cap can't reduce a card to a few pixels. */
const MIN_SCALE = 0.02;
const MAX_FALLBACK_ATTEMPTS = 4;

export function isEncodableImageType(mime: string | undefined | null): mime is EncodableImageType {
  return !!mime && EncodableImageTypes.includes(mime);
}

export function extensionForImageType(type: EncodableImageType): RewritableExtension {
  return ExtensionByType[type];
}

export interface EncodeBitmapOptions {
  readonly type: EncodableImageType;
  readonly quality?: number;
  readonly width?: number;
  readonly height?: number;
}

export interface FitBitmapToBytesOptions {
  readonly maxBytes: number;
  readonly type?: EncodableImageType;
  readonly quality?: number;
  /** Wall-clock budget for the search. On exhaustion the best under-cap result so far is returned. */
  readonly maxDurationMs?: number;
}

type ResizeShape = { width: number, height: number } | { scale: number } | { ensureMaxBytes: number };

export type ResizeOptions = ResizeShape & {
  /**
   * Force a specific output type. When omitted the source type is kept if it is encodable,
   * otherwise JPEG is used — canvas cannot emit anything else, and asking it to silently produces
   * PNG bytes under the wrong label.
   */
  readonly outputType?: EncodableImageType;
  readonly quality?: number;
  readonly maxDurationMs?: number;
};

/**
 * Builds a reusable encoder over a single bitmap. The canvas and 2D context are created once and
 * resized per call, which is what makes the binary search in {@link fitBitmapToBytes} affordable.
 */
function createBitmapEncoder(
  bitmap: ImageBitmap,
  type: EncodableImageType,
  quality: number,
  cancellationToken?: Readonly<CancellationToken>
) {
  const useOffscreen = 'OffscreenCanvas' in window;
  let canvas: HTMLCanvasElement | OffscreenCanvas | undefined;
  let ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | undefined;

  return async (targetWidth: number, targetHeight: number): Promise<Blob> => {
    const width = Math.max(1, Math.round(targetWidth));
    const height = Math.max(1, Math.round(targetHeight));

    canvas ??= useOffscreen ? new OffscreenCanvas(width, height) : document.createElement('canvas');
    // Assigning either dimension resets the surface, so this doubles as the clear between passes.
    canvas.width = width;
    canvas.height = height;
    ctx ??= canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
    if (!ctx) {
      throw new Error('Failed to acquire a 2D canvas context');
    }
    ctx.clearRect(0, 0, width, height);

    if (type === 'image/jpeg') {
      // JPEG has no alpha channel. Without a matte, a transparent PNG or a screenshot composites
      // onto black and the card comes out unreadable.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
    }

    checkCancellation(cancellationToken);
    ctx.drawImage(bitmap, 0, 0, width, height);
    checkCancellation(cancellationToken);

    let blob: Blob | null;
    if (useOffscreen) {
      blob = await (canvas as OffscreenCanvas).convertToBlob({type, quality});
    } else {
      blob = await new Promise<Blob | null>(resolve => {
        (canvas as HTMLCanvasElement).toBlob(resolve, type, quality);
      });
    }

    if (!blob) {
      throw new Error(`Failed to encode image as ${type}`);
    }
    return blob;
  };
}

/** Encodes a decoded bitmap once, at its natural size unless width/height are supplied. */
export async function encodeBitmap(bitmap: ImageBitmap, options: WithCancellation<EncodeBitmapOptions>): Promise<Blob> {
  const encode = createBitmapEncoder(
    bitmap,
    options.type,
    options.quality ?? DEFAULT_ENCODE_QUALITY,
    options.cancellationToken
  );
  return encode(options.width ?? bitmap.width, options.height ?? bitmap.height);
}

/**
 * Encodes a bitmap, shrinking it until the result fits under `maxBytes`.
 *
 * Takes an already-decoded bitmap rather than a Blob so callers that have just decoded something
 * (the HEIF path) can encode once instead of round-tripping through an intermediate file.
 */
export async function fitBitmapToBytes(bitmap: ImageBitmap, options: WithCancellation<FitBitmapToBytesOptions>): Promise<Blob> {

  const {maxBytes, cancellationToken} = options;
  const type = options.type ?? 'image/jpeg';
  const budgetMs = options.maxDurationMs ?? DEFAULT_FIT_BUDGET_MS;
  const startedAt = performance.now();
  const encode = createBitmapEncoder(bitmap, type, options.quality ?? DEFAULT_ENCODE_QUALITY, cancellationToken);

  const fullSize = await encode(bitmap.width, bitmap.height);
  if (fullSize.size <= maxBytes) {
    return fullSize;
  }

  // Best guess scale based on square root of size ratio, against the *encoded* size — the source
  // file's size is not comparable once the output type differs from the input's.
  let scaleGuess = Math.sqrt(maxBytes / fullSize.size);
  console.debug('scale guess', scaleGuess, {encodedSize: fullSize.size, targetSize: maxBytes, type});
  scaleGuess = Math.min(scaleGuess, 1.0); // Ensure it doesn't upscale

  // Perform refinement around the guess
  let lowScale = Math.max(scaleGuess - 0.2, 0.1); // Start slightly lower
  let highScale = Math.min(scaleGuess + 0.2, 1.0); // Start slightly higher
  let bestBlob: Blob | null = null;
  // Smallest over-cap result seen, so the fallback below has somewhere to shrink from.
  let smallestOver: {blob: Blob, scale: number} | null = null;
  const MAX_REFINE_ITERATIONS = 5;
  const MAX_ATTEMPTS = 10;
  let i = 0;

  while (i < MAX_ATTEMPTS && lowScale < highScale - 0.01) {

    checkCancellation(cancellationToken);
    if (i > MAX_REFINE_ITERATIONS) {
      // If we've done too many refine iterations, just take the best we have
      break;
    }

    // Out of wall-clock budget: stop refining and take what we have. A slightly larger upload
    // beats blowing the caller's timeout. Gated on `bestBlob` because without a result there is
    // nothing to degrade *to* — `MAX_ATTEMPTS` remains the bound in that case.
    if (bestBlob && performance.now() - startedAt >= budgetMs) {
      console.debug('fitBitmapToBytes budget exhausted', {budgetMs, attempts: i});
      break;
    }

    const scale = (lowScale + highScale) / 2;
    const resizeStart = performance.now();
    const blob = await encode(bitmap.width * scale, bitmap.height * scale);
    const elapsedTime = performance.now() - resizeStart;
    const size = blob.size;

    console.debug('off by', {
      elapsedTime,
      diff: maxBytes - size,
      isUnder: size < maxBytes,
      scale,
      max: maxBytes,
      size,
      width: bitmap.width,
      height: bitmap.height,
      quality: size / fullSize.size
    });

    if (size > maxBytes) {
      // Too large, shrink further
      if (!smallestOver || size < smallestOver.blob.size) {
        smallestOver = {blob, scale};
      }
      highScale = scale;

      // If it took too long, reduce the step even further based on how many attempts we've made and how long it took
      if (elapsedTime > 1000) {
        highScale -= 0.03 * (MAX_ATTEMPTS - i);
      } else if (elapsedTime > 750) {
        highScale -= 0.02 * (MAX_ATTEMPTS - i);
      } else if (elapsedTime > 500) {
        highScale -= 0.01 * (MAX_ATTEMPTS - i);
      }

      if (bestBlob) {
        i++;
      }
    } else {
      // Small enough, save this blob and try larger scale
      bestBlob = blob;
      lowScale = scale;
      i++;

      // if resize is quick enough, try to get a better result
      if (elapsedTime < 200) {
        continue;
      }

      // if resize is relatively quick, and we're less than 75% of the target size, try to get a better result
      if (elapsedTime < 500 && size < maxBytes * 0.75) {
        continue;
      }
      // if resize is relatively slow, and we're less than 90% of the target size, try to get a better result
      if (elapsedTime < 750 && size < maxBytes * 0.9) {
        continue;
      }
      // either we're close enough or we're taking too long, so break
      break;
    }
  }

  if (!bestBlob && smallestOver) {
    // The bracketed search can finish empty-handed two ways: `lowScale`'s 0.1 floor was an
    // optimistic lower bound that the answer sits below, or the elapsed-time backoff narrowed
    // `highScale` past `lowScale` faster than the search converged — which is exactly what a slow
    // device does. Shrink directly from the smallest result instead: each step targets the cap by
    // area ratio, so it lands in one or two encodes.
    let {blob, scale} = smallestOver;
    for (let attempt = 0; attempt < MAX_FALLBACK_ATTEMPTS && blob.size > maxBytes && scale > MIN_SCALE; attempt++) {
      checkCancellation(cancellationToken);
      scale = Math.max(scale * Math.sqrt(maxBytes / blob.size) * 0.9, MIN_SCALE);
      blob = await encode(bitmap.width * scale, bitmap.height * scale);
      console.debug('fallback shrink', {scale, size: blob.size, max: maxBytes});
    }
    if (blob.size <= maxBytes) {
      bestBlob = blob;
    }
  }

  if (!bestBlob) {
    throw new Error('Failed to resize image under the requested size');
  }
  return bestBlob;
}

export async function resizeImage<T extends Blob | File>(file: T, options: WithCancellation<ResizeOptions>): Promise<T extends File ? File : Blob> {

  const cancellationToken = options.cancellationToken;
  checkCancellation(cancellationToken);

  const keepsSourceType = !options.outputType || options.outputType === file.type;
  if ('ensureMaxBytes' in options && options.ensureMaxBytes >= file.size && keepsSourceType) {
    // Already small enough and no transcode requested — don't pay for a decode.
    return file as unknown as T extends File ? File : Blob;
  }

  // `imageOrientation` must be passed explicitly: the spec default changed from 'none' to
  // 'from-image' and engines still differ, so leaving it off is how EXIF-rotated photos end up
  // sideways.
  const bitmap = await createImageBitmap(file, {imageOrientation: 'from-image'});
  try {
    checkCancellation(cancellationToken);
    const outputType: EncodableImageType = options.outputType
      ?? (isEncodableImageType(file.type) ? file.type : 'image/jpeg');

    let blob: Blob;
    if ('width' in options && 'height' in options) {
      blob = await encodeBitmap(bitmap, {
        type: outputType, quality: options.quality, width: options.width, height: options.height, cancellationToken
      });
    } else if ('scale' in options) {
      blob = await encodeBitmap(bitmap, {
        type: outputType,
        quality: options.quality,
        width: bitmap.width * options.scale,
        height: bitmap.height * options.scale,
        cancellationToken
      });
    } else if ('ensureMaxBytes' in options) {
      blob = await fitBitmapToBytes(bitmap, {
        maxBytes: options.ensureMaxBytes,
        type: outputType,
        quality: options.quality,
        maxDurationMs: options.maxDurationMs,
        cancellationToken
      });
    } else {
      throw new Error('Invalid options');
    }

    if (file instanceof File) {
      const name = outputType === file.type
        ? file.name
        : withExtension(file.name, extensionForImageType(outputType));
      return new File([blob], name, {type: outputType}) as T extends File ? File : Blob;
    }
    return blob as T extends File ? File : Blob;
  } finally {
    // Bitmaps hold decoded pixels off-heap; without this a 12MP photo stays resident until GC.
    bitmap.close();
  }
}
